<template>
  <div id="tabelaRespostasDeAvaliacao" class="w-100 m-0 d-flex flex-column align-items-stretch border rounded">
    <HeaderSagres titulo="Respostas" class="w-100 pl-3 respostas-header-serigy">
      <template #customContent>
        <div class="respostas-meta-serigy">
          <small>Total: {{ totalCheck }}/{{ totalRowsItens }}</small>
          <b-progress :variant="progressColor" class="respProgress border rounded">
            <b-progress-bar :value="progress" />
          </b-progress>
          <small>Fim da Avaliação: {{ dataFim }}</small>
          <btn-download botao :fazerDownload="downloadJson" texto="Baixar JSON" />
          <btn-download botao :fazerDownload="downloadCsv" texto="Baixar CSV" />
        </div>
      </template>
    </HeaderSagres>
    <b-table :fields="fieldsItens" :items="pgRespostas" :busy="tableBusyItens" :current-page="currentPageItens" hover
      no-border-collapse striped small sort-by="" stacked="lg" responsive show-empty>
      <template #empty>
        <span>Não foram encontrados dados.</span>
      </template>
      <template #emptyfiltered>
        <span>Não foram encontrados dados.</span>
      </template>
      <template #table-busy>
        <div class="text-center text-dark">
          <b-spinner class="align-middle"></b-spinner>
          <strong class="loading-text">Carregando...</strong>
        </div>
      </template>

      <template v-slot:cell(actions)="row">
        <font-awesome-icon :icon="row.item.icone" class="mx-2" :class="row.item.corIcone" />
        <b-link v-if="!!row.item.codigo" class="text-dark" @click="row.toggleDetails">
          <font-awesome-icon :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'" />
        </b-link>
      </template>
      <template #row-details="row">
        <div class="mx-3 d-flex flex-column align-items-start border rounded">
          <p>
            <span class="font-weight-bold">Código: </span>
            {{ row.item.codigo }}
          </p>
          <p>
            <span class="font-weight-bold">Mensagem: </span>{{ row.item.mensagem }}
          </p>
        </div>
      </template>
    </b-table>
    <TablePagination v-if="totalRowsItens > pageSize" :total-rows="totalRowsItens" :per-page="pageSize"
      :firstCurrentPg="currentPageItens" class="" @pageChange="pageChange" />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { UtilsTS } from "@/utilsts";
import serigyService from "@/services/administracao/serigyService";
import { TableField } from "@/types/table-field";
import { fieldsInvokerRespostas } from "@/types/administracao/serigy/invoker/fields-invoker-respostas";
import { RespostaAvaliacao, RespostaAvaliacaoJson } from "@/types/administracao/serigy/invoker/resposta-avaliacao";
import TablePagination from "@/components/atos-pessoal/TablePagination.vue";
import { PropType } from "vue";
import { Avaliacao } from "@/types/administracao/serigy/invoker/avaliacao";
import BtnDownload from '@/components/shared/btn-download.vue'
import HeaderSagres from "@/components/shared/HeaderSagres.vue";

const CompProps = Vue.extend({
  name: "TabelaRespostas",
  components: { BtnDownload, HeaderSagres, TablePagination },
  props: {
    avaliacao: {
      type: Object as PropType<Avaliacao>,
      required: true,
    },
    isAvaliacaoGeral: {
      type: Boolean,
      default: false,
    }
  },
});

@Component
export default class TabelaRespostas extends mixins(UtilsTS, CompProps) {
  fieldsItens: TableField[] = [...fieldsInvokerRespostas];
  respostas: RespostaAvaliacao[] = [];
  respostaAvaliacaoJson: RespostaAvaliacaoJson[] = [];

  pageSize = 10;
  currentPageItens = 1;
  tableBusyItens = false;

  get dataFim(): string {
    return this.avaliacao.dhFim?.toLocaleString("pt-br") || "";
  }
  get pgRespostas(): RespostaAvaliacao[] {
    if (this.totalRowsItens <= this.pageSize) {
      return [...this.respostas];
    }

    const start = (this.currentPageItens - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.respostas.slice(start, end);
  }
  get progress(): number {
    return (this.totalCheck / this.totalRowsItens) * 100;
  }
  get progressColor(): string {
    return this.progress >= 50 ? "success" : this.progress >= 20 ? "warning" : "danger";
  }
  get totalRowsItens(): number {
    return this.respostas.length;
  }
  get totalCheck(): number {
    return this.respostas.filter((r) => r.ponto).length;
  }


  async mounted(): Promise<void> {
    this.loadData();
  }

  async downloadJson(): Promise<void> {
    try {
      const contentStr = JSON.stringify(this.respostaAvaliacaoJson, null, 2);
      const blob = new Blob([contentStr], { type: "application/json;charset=utf-8" });
      this.download(`respostas-avaliacao-${this.avaliacao.id}.json`, blob);
    } catch (e: unknown) {
      this.tratarError(e)
    }
  }

  async downloadCsv(): Promise<void> {
    try {
      const contentStr = this.jsonToCSV(this.respostaAvaliacaoJson);
      const blob = new Blob(["\uFEFF" + contentStr], { type: "text/csv" });
      this.download(`respostas-avaliacao-${this.avaliacao.id}.csv`, blob);
    } catch (e: unknown) {
      this.tratarError(e)
    }
  }

  async loadData(): Promise<void> {
    try {
      this.tableBusyItens = true;
      const retorno = this.isAvaliacaoGeral ? 
        await serigyService.listarRespostasAvaliacaoPorAvaliacaoGeral(
          this.avaliacao.id,
        )
      : await serigyService.listarRespostasAvaliacao(
        this.avaliacao.id,
      );
      this.respostas = retorno.respostaAvaliacaos
      this.respostaAvaliacaoJson = retorno.respostaAvaliacaoJson
    } catch (e) {
      this.respostas = [];
      this.tratarError(e);
    } finally {
      this.tableBusyItens = false;
    }
  }
  async pageChange(value: number): Promise<void> {
    this.currentPageItens = value;
  }

  jsonToCSV<T extends Record<string, any>>(data: T[]): string {
    if (!data || data.length === 0) return "";

    type Extended = T & { erro?: string, detalhe?: string };

    const processedData: Extended[] = data.map(item => {
      const newItem: Extended = { ...item };

      if (typeof newItem.detalhe === "string") {
        const index = newItem.detalhe.indexOf("|");

        if (index !== -1) {
          newItem.erro = newItem.detalhe.slice(0, index);
          newItem.detalhe = newItem.detalhe.slice(index + 1);
        }
      }

      return newItem;
    });

    let headers = Array.from(
      new Set(processedData.flatMap(obj => Object.keys(obj)))
    );

    const hasErro = headers.includes("erro");
    const hasDetalhe = headers.includes("detalhe");

    headers = headers.filter(h => h !== "erro" && h !== "detalhe");

    if (hasErro) headers.push("erro");
    if (hasDetalhe) headers.push("detalhe");

    const escapeValue = (value: any): string => {
      if (value === null || value === undefined) return "";

      const stringValue = String(value);

      if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    };

    const csvRows = [
      headers.join(";"),
      ...processedData.map(row =>
        headers.map(header => escapeValue(row[header])).join(";")
      )
    ];

    return "\uFEFF" + csvRows.join("\n");
  }
}
</script>
<style scoped>
.respProgress {
  width: 15rem;
}

.respostas-meta-serigy {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

/deep/ .respostas-header-serigy {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 0.5rem;
}

/deep/ .respostas-header-serigy > span {
  white-space: nowrap;
  word-break: normal;
}

/deep/ .respostas-header-serigy .custom-content {
  width: 100%;
  margin-right: 0 !important;
  justify-content: flex-start !important;
  align-items: center !important;
  flex-direction: row !important;
  flex-wrap: wrap !important;
}

/deep/ .respostas-header-serigy,
/deep/ .respostas-header-serigy * {
  word-break: normal;
}

@media screen and (max-width: 575.98px) {
  .respProgress {
    width: 100%;
    min-width: 10rem;
  }
}
</style>
